import React from "react";
import { Formik } from "formik";
import { Form } from "./form";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { PrepFqQuestion } from "../../../../../models/prepFqQuiz";
import {
  IncomingScoringData,
  PrepFqAnswer,
  PrepFqProfile,
} from "../../../../../models/users";
import { rSetAlert } from "../../../../../reducers/session/actions";
import * as Yup from "yup";
import { useAuth } from "../../../../../firebase/useAuth";
import useUser from "../../../../../firebase/useUser";
import usePrepFqProfile from "../../../fetchers/usePrepFqProfile";
import usePrepFqAnswers from "../../../fetchers/usePrepFqAnswers";
import { calculateAge } from "../../../../../utils/date";
import logger from "../../../../../utils/logger";
import { PwgError } from "../../../../../models/common";
import { useRouter } from "next/router";
import { doc, getFirestore, writeBatch } from "firebase/firestore";
import { firebaseApp } from "../../../../../firebase/firebaseClient";
import { getFunctions, httpsCallable } from "firebase/functions";

interface Props {
  questions: PrepFqQuestion[];
}

const PrepFqFormMini: React.FC<Props> = ({ questions }) => {
  const { t } = useTranslation(["prepFq", "common"]);
  const dispatch = useDispatch();
  const router = useRouter();
  const authUser = useAuth();
  const { user } = useUser(authUser?.user?.uid);
  const { profile, mutate: mutateProfile } = usePrepFqProfile(user?.user_id);
  const { answers, mutate: mutateAnswers } = usePrepFqAnswers(
    profile?.prep_fq_profile_id,
    questions
  );
  if (!answers) {
    return null;
  }

  const setUpValues = (
    questions: PrepFqQuestion[],
    answers: PrepFqAnswer[]
  ) => {
    const userValues = {};
    questions.forEach((question) => {
      if (
        question.question_type === "dropdown" ||
        question.question_type === "radio"
      ) {
        userValues[question.question_id] = "";
      } else if (question.question_type === "selection") {
        question.response_options.forEach((response, index) => {
          userValues[`${question.question_id}_option_${index + 1}`] = false;
        });
      }
      if (question.has_prerequisite_question) {
        userValues[question.prerequisite_question.question_id] = "";
      }
    });
    answers.forEach((answer) => {
      if (
        answer.question_type === "dropdown" ||
        answer.question_type === "radio"
      ) {
        userValues[answer.question_id] = answer.response_value;
      } else if (answer.question_type === "selection") {
        answer.selection_values.forEach((value, index) => {
          userValues[`${answer.question_id}_option_${index + 1}`] = value;
        });
      }
      if (answer.has_prerequisite_answer) {
        userValues[answer.prerequisite_answer.question_id] =
          answer.prerequisite_answer.response_value;
      }
    });
    return userValues;
  };
  const values = setUpValues(questions, answers);

  const setUpValidation = (questions: PrepFqQuestion[]) => {
    const obj = {};
    questions.forEach((question) => {
      if (question.has_prerequisite_question) {
        if (question.question_type === "selection") {
          question.response_options.forEach((option, index) => {
            obj[`${question.question_id}_option_${index + 1}`] =
              Yup.boolean().when(
                [question.prerequisite_question.question_id],
                ([prerequisiteValue], schema) => {
                  return prerequisiteValue === "1"
                    ? schema.test(
                        "oneOfRequired",
                        t("common:labels.selectAnOption"),
                        function (item) {
                          const atLeastOneTrue = question.response_options.some(
                            (_, index) =>
                              Boolean(
                                this.parent[
                                  `${question.question_id}_option_${index + 1}`
                                ]
                              )
                          );
                          return atLeastOneTrue || item;
                        }
                      )
                    : schema;
                }
              );
          });
        } else {
          obj[question.question_id] = Yup.string()
            .nullable()
            .when(
              [question.prerequisite_question.question_id],
              ([prerequisiteValue], schema) => {
                return prerequisiteValue === "1"
                  ? schema.required(t("common:labels.isRequired"))
                  : schema;
              }
            );
        }
        obj[question.prerequisite_question.question_id] = Yup.string()
          .nullable()
          .required(`${t("common:labels.isRequired")}`);
      } else if (
        question.has_requirements &&
        question.requirements.type === "question"
      ) {
        obj[question.question_id] = Yup.string()
          .nullable()
          .when(
            [question.requirements.value.toString()],
            ([requirementValue], schema) => {
              return requirementValue === "1" ? schema : schema;
            }
          );
      } else if (question.question_type === "selection") {
        question.response_options.forEach((option, index) => {
          obj[`${question.question_id}_option_${index + 1}`] =
            Yup.boolean().test(
              "oneOfRequired",
              t("common:labels.selectAnOption"),
              function (item) {
                const atLeastOneTrue = question.response_options.some(
                  (item, index) =>
                    (this.parent[
                      `${question.question_id}_option_${index + 1}`
                    ] = true)
                );
                return atLeastOneTrue || item;
              }
            );
        });
      } else {
        obj[question.question_id] = Yup.string()
          .nullable()
          .required(`${t("common:labels.isRequired")}`);
      }
    });
    return Yup.object({ ...obj });
  };
  const validationSchema = setUpValidation(questions);
  const isNumeric = (str: string) => {
    if (typeof str != "string") return false; // we only process strings!
    const int = parseInt(str);
    if (isNaN(int)) {
      return null;
    } else {
      return int;
    }
  };
  const onSubmit = async (submission: typeof values) => {
    try {
      //SAVE ANSWERS
      // save the current prepFq page
      const answers: PrepFqAnswer[] = [];
      questions.forEach((question) => {
        let answer: PrepFqAnswer;
        if (
          question.question_type === "dropdown" ||
          question.question_type === "radio"
        ) {
          answer = {
            answer_id: question.question_id,
            question_id: question.question_id,
            date_created: new Date().getTime(),
            response_value:
              typeof submission[question.question_id] === "string"
                ? isNumeric(submission[question.question_id])
                : submission[question.question_id],
            has_prerequisite_answer: false,
            prerequisite_answer: null,
            question_type: "dropdown",
            selection_values: [],
          };
        } else if (question.question_type === "selection") {
          answer = {
            answer_id: question.question_id,
            question_id: question.question_id,
            date_created: new Date().getTime(),
            has_prerequisite_answer: false,
            prerequisite_answer: null,
            question_type: "selection",
            response_value: null,
            selection_values: question.response_options.map((option, index) => {
              return submission[`${question.question_id}_option_${index + 1}`];
            }),
          };
        }
        if (question.has_prerequisite_question) {
          const preReqAnswer: PrepFqAnswer = {
            answer_id: question.prerequisite_question.question_id,
            question_id: question.prerequisite_question.question_id,
            date_created: new Date().getTime(),
            response_value:
              typeof submission[question.prerequisite_question.question_id] ===
              "string"
                ? isNumeric(
                    submission[question.prerequisite_question.question_id]
                  )
                : submission[question.prerequisite_question.question_id],
            selection_values: [],
            has_prerequisite_answer: false,
            prerequisite_answer: null,
            question_type: "dropdown",
          };
          answer.has_prerequisite_answer = true;
          answer.prerequisite_answer = preReqAnswer;
        }
        answers.push(answer as any);
      });

      const profileUpdate: PrepFqProfile = {
        date_last_updated: new Date().getTime(),
      } as PrepFqProfile;

      if (
        answers.every(
          (answer) =>
            (answer.response_value !== null &&
              (answer.question_type === "dropdown" ||
                answer.question_type === "radio")) ||
            (answer.selection_values.length !== 0 &&
              answer.question_type === "selection")
        )
      ) {
        const firestore = getFirestore(firebaseApp);
        const batch = writeBatch(firestore);
        answers.forEach((answer) => {
          batch.set(
            doc(
              firestore,
              "prep_fq_profiles",
              profile?.prep_fq_profile_id,
              "answers",
              answer.answer_id
            ),
            answer
          );
        });
        batch.update(
          doc(firestore, "prep_fq_profiles", profile?.prep_fq_profile_id),
          profileUpdate as any
        );
        await batch.commit();
        mutateAnswers();
        const functions = getFunctions(firebaseApp);
        const onCalculatePrepFqScore = httpsCallable(
          functions,
          "calculatePrepFqScore"
        );
        await onCalculatePrepFqScore({
          prep_fq_profile_id: profile?.prep_fq_profile_id,
          gender: user.gender,
          user_id: user.user_id,
          private_key: "",
          age: calculateAge(user?.date_of_birth),
        } as IncomingScoringData);
        mutateProfile();
      }
    } catch (error) {
      const pwgError: PwgError = {
        user_id: authUser?.user?.uid,
        ssr: false,
        context: "onSubmit: PrepFqFormMini",
        date_created: new Date().getTime(),
        error: error?.toString(),
        pathname: router.asPath,
      };
      await logger.e(
        "🚀 ~ file: PrepFqFormMini.tsx ~ line 284 ~ onSubmit ~ error",
        error,
        pwgError
      );
      dispatch(
        rSetAlert({
          key: new Date().getTime().toString(),
          message: t("prepFq:error"),
          type: "error",
        })
      );
    }
  };

  return (
    <React.Fragment>
      <Formik
        onSubmit={onSubmit}
        initialValues={values}
        validationSchema={validationSchema}
        validateOnChange={false}
        enableReinitialize
      >
        {(props) => <Form {...{ ...props, questions }} />}
      </Formik>
    </React.Fragment>
  );
};

export default PrepFqFormMini;
